import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { DialogService } from '@ngneat/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Vehicle } from 'rest-client';

import { VehicleService } from '@app/modules/core/services/vehicle/vehicle.service';
import { DIALOG_CONFIG } from '@app/modules/core/utils/dialog.config';
import { GoogleTagManagerService } from '@core/services/analytics/google-tag-manager.service';

import { VehicleReturned } from '../../models/car-service-data.model';
import { FeedbackDialogComponent } from '../feedback-dialog/feedback-dialog.component';

/**
 * > Creates new vehicle by Plate (It tries UMC Autoidentify)
 */
@UntilDestroy()
@Component({
  selector: 'bkg-vehicle-dialog-license-plate',
  templateUrl: './vehicle-dialog-license-plate.component.html',
})
export class VehicleDialogLicensePlateComponent implements OnInit {
  @Output() newVehicleReturn = new EventEmitter<VehicleReturned>();
  @Output() errorReturn = new EventEmitter<HttpErrorResponse>();
  @Output() addNewVehicleViewReturn = new EventEmitter<boolean>();
  @Output() showUmcSelectorsReturn = new EventEmitter<boolean>();
  @Output() plateNotFoundedReturn = new EventEmitter<string>();
  @Output() vehicleNotFoundedReturn = new EventEmitter<number>();
  @Input() isLogged: boolean;
  @Input() addMoreInfoLicense: boolean;
  @Input() clientId: number;

  newVehiclePlateForm: FormGroup;
  plateNotFounded: string;
  notFoundedVehicleID: number;
  formSending: boolean = false

  constructor(
    private vehicleService: VehicleService,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private dialog: DialogService,
    private googleTagManagerService: GoogleTagManagerService
  ) {}

  ngOnInit(): void {
    this.buildNewVehiclePlateForm();
  }

  private buildNewVehiclePlateForm = (): void => {
    this.newVehiclePlateForm = this.formBuilder.group({
      plate: [null, [Validators.required]],
    });
  };

  submitNewVehiclePlate = (): void => {
    this.formSending = true;
    const plate = this.newVehiclePlateForm.get('plate').value as string;
    this.createVehicle(plate);
  };

  onForgottenCarLicense = (): void => {
    this.googleTagManagerService.pushOnDataLayer({
      event: 'no-encuentro-matricula-link',
    });
    this.googleTagManagerService.pushBookingActionOnDataLayer({
      event_name: 'no-encuentro-matricula-link',
    });
    if (this.plateNotFounded) {
      this.plateNotFoundedReturn.emit(this.plateNotFounded);
      this.vehicleNotFoundedReturn.emit(this.notFoundedVehicleID);
    }
    this.showUmcSelectorsReturn.emit(true);
  };

  private createVehicle = (new_plate_value: string): void => {
    const payload = this.createPayload(new_plate_value);
    if (this.clientId) {
      payload['client_id'] = this.clientId.toString();
    }
    this.vehicleService
      .createVehicle(payload, true)
      .pipe(untilDestroyed(this))
      .subscribe(
        (vehicle: Vehicle) => {
          this.formSending = false;
          if (!vehicle.id_gt_vehicle_information) {
            this.plateNotFounded = new_plate_value;
            this.notFoundedVehicleID = vehicle.id;
            this.openWarningFeedbackDialog();
          } else {
            this.openFeedbackDialog(vehicle);
            this.googleTagManagerService.pushOnDataLayer({
              event: 'vehiculo-seleccionado-matricula',
              'vehiculo-marca': vehicle?.vehicle_information?.make_description,
              'vehiculo-modelo':
                vehicle?.vehicle_information?.model_description,
            });
            this.googleTagManagerService.pushBookingActionOnDataLayer({
              event_name: 'vehiculo-seleccionado-matricula',
              'vehiculo-marca': vehicle?.vehicle_information?.make_description,
              'vehiculo-modelo':
                vehicle?.vehicle_information?.model_description,
            });
            this.newVehicleReturn.emit({ vehicle: vehicle, type: 'license' });
            this.addNewVehicleViewReturn.emit(false);
          }
        },
        (error: HttpErrorResponse) => this.errorReturn.emit(error)
      );
  };

  private openWarningFeedbackDialog(): void {
    this.googleTagManagerService.pushOnDataLayer({
      event: 'matricula-error',
    });

    this.googleTagManagerService.pushBookingActionOnDataLayer({
      event_name: 'matricula-error',
    });

    this.dialog
      .open(FeedbackDialogComponent, {
        ...DIALOG_CONFIG,
        size: 'md',
        enableClose: false,
        data: {
          icon: 'assets/images/icons/warning-icon.svg',
          title: this.translateService.instant(
            'sharedModule.bkgFeedbackDialog.vehicleDangerTitle'
          ) as string,
          body: this.translateService.instant(
            'sharedModule.bkgFeedbackDialog.vehicleDangerBody'
          ) as string,
          mode: 'danger',
        },
      })
      .afterClosed$.pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.plateNotFounded) {
          this.plateNotFoundedReturn.emit(this.plateNotFounded);
          this.vehicleNotFoundedReturn.emit(this.notFoundedVehicleID);
        }
        this.addNewVehicleViewReturn.emit(false);
        this.showUmcSelectorsReturn.emit(true);
      });
  }

  private openFeedbackDialog(vehicle: Vehicle): void {
    return;
    const plate = vehicle?.plate ? vehicle.plate : '';
    const model =
      vehicle?.vehicle_information &&
      vehicle?.vehicle_information.make_description
        ? vehicle?.vehicle_information.make_description +
          ' ' +
          vehicle?.vehicle_information.model_description
        : '';
    this.dialog.open(FeedbackDialogComponent, {
      ...DIALOG_CONFIG,
      size: 'md',
      enableClose: false,
      data: {
        icon: 'assets/images/icons/ok-icon.svg',
        title: this.translateService.instant(
          'sharedModule.bkgFeedbackDialog.vehicleTitle'
        ) as string,
        body: this.translateService.instant(
          'sharedModule.bkgFeedbackDialog.vehicleBody',
          { plate: plate, model: model }
        ) as string,
      },
    });
  }

  private createPayload = (new_plate_value: string): Record<string, string> => {
    return this.isLogged
      ? { plate: new_plate_value }
      : { plate: new_plate_value, type: null };
  };
}
